<script setup lang="ts">
import { BaseButton } from '@qic/ui-components';
import ReviewStarIcon from '~/components/icons/ReviewStarIcon.vue';
import IconArrowRight from '~/components/icons/IconArrowRight.vue';

const { locale } = useI18n();

interface Button {
  title: string;
  link: string;
}
interface Item {
  id: number;
  rating: number;
  text: string;
  username: string;
  userinfo?: string;
  date: string;
  avatar: {
    documentId: string,
    height: number,
    id: number,
    mime: string,
    size: number,
    url: string,
    width: number,
  };
  source: {
    documentId: string,
    height: number,
    id: number,
    mime: string,
    size: number,
    url: string,
    width: number,
  };
  logo: {
    documentId: string,
    height: number,
    id: number,
    mime: string,
    size: number,
    url: string,
    width: number,
  };
  link: string;
  marked?: boolean;
}
const props = defineProps<{
  title: string;
  description: string;
  button?: Button;
  link?: string;
  rating_text?: string;
  rating_stars?: {
    documentId: string,
    height: number,
    id: number,
    mime: string,
    size: number,
    url: string,
    width: number,
  };
  review_cards: Item[];
}>();


function formatDate (date = '') {
  try {
    const isEn = locale.value === 'en';
    const dateFormat = new Date(date);

    const localeCode = isEn ? 'en-GB' : 'ar-QA';
    const options = { month: 'short' } as Intl.DateTimeFormatOptions;
    const month = dateFormat.toLocaleDateString(localeCode, options);

    const comma = isEn ? ' ' : ' ';
    return (
      dateFormat.getDate() + ' ' + month + comma + dateFormat.getFullYear()
    );
  } catch (e) {
    console.error(e);
    return '';
  }
}

function getStarType (review: any, index: number): String {
  const rating = Number(review?.rating) || 5;
  const star = index;

  if (rating > star - 1 && rating < star) return 'half';
  if (rating < star) return 'empty';
  return 'full';
}
</script>
<template>
  <section class="landing-reviews section-offset" dir="auto">
    <div class="container">
      <div class="landing-reviews__title">
        <h3 v-html="props.title" />
        <div v-if="props.description" class="subtitle" v-html="props.description" />

        <div class="landing-reviews__links">
          <a v-if="props.link" :href="props.link" target="_blank">
            <span>{{ props.rating_text }}</span>
          </a>
          <div v-else>
            <span>{{ props.rating_text }}</span>
          </div>
          <div v-if="props.rating_stars" class="landing-reviews__stars">
            <img :src="props.rating_stars.url">
          </div>
        </div>
      </div>
      <div class="landing-reviews__wrapper">
        <div
          v-for="item in props.review_cards" :key="item.id" class="landing-reviews__item"
          :class="{ 'marked': item.marked }"
        >
          <div v-if="item.rating && !item.marked" class="review-rating">
            <ReviewStarIcon v-for="index in 5" :variant="getStarType(item, index)" color="#FAB61A" />
          </div>
          <div v-if="item.marked && item.logo" class="review-logo">
            <img :src="item.logo.url">
          </div>
          <div class="landing-reviews__text">
            {{ item.text }}

            <div v-if="item.marked && item.link" class="showmore">
              <a class="btn" :href="item.link">
                <BaseButton small icon="none" class="">
                  {{ $t('main.reviews.read_story') }}
                  <IconArrowRight :color="'#494BB5'" />
                </BaseButton>
              </a>
            </div>
          </div>
          <div class="landing-reviews__user">
            <div class="picture">
              <img v-if="item.avatar" :alt="item.username" :src="item.avatar.url">
            </div>
            <div>
              <div class="name">
                {{ item.username }}
              </div>
              <div class="date">
                {{ item.userinfo ? item.userinfo : formatDate(item.date) }}
              </div>
            </div>
            <div v-if="item.source" class="source">
              <img :src="item.source.url">
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import 'ReviewsBlock.scss';
</style>
